import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { MdFacebook, MdEmail, MdSms, MdInfoOutline } from 'react-icons/md';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { getIntegrationStatus } from '../../utils/oauthHelper';
import type { RootState } from '../../redux/store';
import type { SocialMedium } from './types';

interface PlatformSelectorProps {
  selectedPlatforms: SocialMedium[];
  onToggle: (platform: SocialMedium) => void;
  isTikTokActive?: boolean;
  showTikTokInfo?: boolean;
  onTikTokInfoClick?: () => void;
}

const PlatformSelector: React.FC<PlatformSelectorProps> = ({
  selectedPlatforms,
  onToggle,
  isTikTokActive,
  showTikTokInfo,
  onTikTokInfoClick
}) => {
  // Get integrations from Redux (already fetched by Playground component)
  const { data: apiIntegrations } = useSelector(
    (state: RootState) => state.integrations
  );

  // All possible platforms with their UI datax`
  const allPlatforms: { id: SocialMedium; name: string; icon: React.ReactNode; color: string }[] = [
    { id: 'facebook', name: 'Facebook', icon: <MdFacebook size={24} />, color: '#1877F2' },
    { id: 'instagram', name: 'Instagram', icon: <FaInstagram size={24} />, color: '#E4405F' },
    { id: 'tiktok', name: 'TikTok', icon: <FaTiktok size={24} />, color: '#000000' },
    { id: 'twitter', name: 'X', icon: <FaXTwitter size={24} />, color: '#000000' },
    { id: 'email', name: 'Email', icon: <MdEmail size={24} />, color: '#EA4335' },
    { id: 'sms', name: 'Sms', icon: <MdSms size={24} />, color: '#EA4335'}
  ];

  // Filter to only show connected platforms (state: "OK") + email always
  const connectedMediums = useMemo(() => {
    return allPlatforms.filter((platform) => {
      
      // For other platforms, check if they're connected
      const { isActive } = getIntegrationStatus(apiIntegrations, platform.id);
      return isActive;
    });
  }, [apiIntegrations]);

  // When the TikTok settings panel is active, hide all other platforms and only show the TikTok tile
  const isTikTokSelected = selectedPlatforms.includes('tiktok');
  const isCompactTikTokView = !!isTikTokActive;
  const socialMediums = isCompactTikTokView
    ? connectedMediums.filter((platform) => platform.id === 'tiktok')
    : connectedMediums;

  return (
    <div className={isCompactTikTokView ? 'flex items-center gap-4' : 'space-y-4'}>
      <h3 className="text-sm font-normal text-gray-300 flex-shrink-0">Select Platforms</h3>
      <div className={isCompactTikTokView ? 'flex items-center gap-3' : 'grid grid-cols-3 gap-3'}>
        {socialMediums.map((medium) => (
          <button
            key={medium.id}
            type="button"
            onClick={() => onToggle(medium.id)}
            className={`flex cursor-pointer items-center space-x-3 rounded-lg border-1 transition-colors ${
              isCompactTikTokView ? 'px-2 py-2' : 'p-3'
            } ${
              selectedPlatforms.includes(medium.id)
                ? 'border-brand-primary bg-brand-primary/10'
                : 'border-gray-600 bg-surface-dark hover:border-gray-500'
            }`}
          >
            <div style={{ color: medium.color }}>{medium.icon}</div>
            <span className="text-white text-xs">{medium.name}</span>
            {medium.id === 'tiktok' && isTikTokSelected && showTikTokInfo && (
              <span
                role="button"
                title="View / edit TikTok default post settings"
                onClick={(e) => {
                  e.stopPropagation();
                  onTikTokInfoClick?.();
                }}
                className="text-gray-300 hover:text-brand-primary transition-colors cursor-pointer"
              >
                <MdInfoOutline size={16} />
              </span>
            )}
            {selectedPlatforms.includes(medium.id) && (
              <div className="ml-auto w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PlatformSelector;
